import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { neighborTask } from '../../app/task-nav';
import { useEscapeLayer } from '../../components/ui/escape';
import { isTypingTarget, useIsDesktop } from '../../lib/hooks';
import { TaskDetail } from './TaskDetail';

/** Елементи, клік по яких не закриває картку: самі задачі, спливні шари, діалоги, верхня панель, сповіщення. */
const KEEP_OPEN = '[data-ers-layer], dialog, [role="dialog"], [role="menu"], [role="listbox"], [data-task-item], [data-keep-panel], #app-topbar';

/**
 * Картка задачі поверх переліку чи дошки, як в Asana: на широкому екрані — бічна панель на пів екрана
 * (перелік лишається видимим і прокручуваним), на телефоні й планшеті — на весь екран.
 * Закриття: хрестик, Esc (якщо курсор у полі — спершу знімає фокус) або клік поза карткою.
 * J/K (або ↑/↓, коли фокус на самій картці) — сусідня задача, картка не закривається.
 */
export function TaskPanel({ taskId, onClose, onNavigate }: { taskId: string; onClose: () => void; onNavigate: (id: string) => void }) {
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const ref = useRef<HTMLElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEscapeLayer(true, () => {
    const active = document.activeElement;
    if (isTypingTarget(active) && ref.current?.contains(active)) {
      (active as HTMLElement).blur();
      return;
    }
    onClose();
  });

  // Фокус у панель під час відкриття, повернення — на елемент, з якого відкрили.
  useEffect(() => {
    opener.current = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => ref.current?.focus({ preventScroll: true }), 0);
    return () => {
      window.clearTimeout(t);
      const back = opener.current;
      // Після закриття: коли з решти сторінки знято інертність (ефект нижче) і фокус ніхто не забрав.
      window.setTimeout(() => {
        const active = document.activeElement;
        if (back && back.isConnected && (!active || active === document.body)) back.focus({ preventScroll: true });
      }, 0);
    };
  }, []);

  // На телефоні панель модальна: решта сторінки інертна.
  useEffect(() => {
    if (desktop) return;
    const els = ['app-main', 'app-topbar', 'app-bottomnav', 'app-sidebar'].map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    els.forEach((el) => el.setAttribute('inert', ''));
    return () => els.forEach((el) => el.removeAttribute('inert'));
  }, [desktop]);

  // Клік поза карткою закриває її (лише клік: перетягування, прокручування й виділення тексту — ні).
  useEffect(() => {
    if (!desktop) return;
    let start: { x: number; y: number; target: Element } | null = null;
    const onDown = (e: PointerEvent) => {
      start = e.button === 0 && e.target instanceof Element ? { x: e.clientX, y: e.clientY, target: e.target } : null;
    };
    const onUp = (e: PointerEvent) => {
      const s = start;
      start = null;
      if (!s || e.button !== 0) return;
      if (Math.abs(e.clientX - s.x) > 5 || Math.abs(e.clientY - s.y) > 5) return;
      if (!s.target.isConnected || ref.current?.contains(s.target) || s.target.closest(KEEP_OPEN)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointerup', onUp, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointerup', onUp, true);
    };
  }, [desktop, onClose]);

  // J/K — наступна/попередня задача; ↑/↓ — коли фокус на самій картці (не в полі чи списку).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('dialog[open]')) return;
      const onPanel = e.target === ref.current || e.target === document.body;
      let dir: 1 | -1 | 0 = 0;
      if (e.code === 'KeyJ') dir = 1;
      else if (e.code === 'KeyK') dir = -1;
      else if (onPanel && e.key === 'ArrowDown') dir = 1;
      else if (onPanel && e.key === 'ArrowUp') dir = -1;
      if (!dir) return;
      const next = neighborTask(taskId, dir);
      if (!next) return;
      e.preventDefault();
      onNavigate(next);
      document.querySelector<HTMLElement>(`[data-task-id="${next}"]`)?.scrollIntoView({ block: 'nearest' });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [taskId, onNavigate]);

  // Після перемикання задачі фокус лишається на картці — J/K і ↑/↓ працюють далі.
  useEffect(() => {
    const el = ref.current;
    if (el && (document.activeElement === document.body || !el.contains(document.activeElement))) {
      if (document.activeElement?.closest('[data-task-item]')) return;
      el.focus({ preventScroll: true });
    }
  }, [taskId]);

  return (
    <aside
      ref={ref}
      tabIndex={-1}
      aria-label="Картка задачі"
      data-tour="task-card"
      className="ers-panel fixed inset-0 z-40 flex flex-col bg-surface outline-none lg:top-[var(--topbar-h)] lg:right-0 lg:bottom-0 lg:left-auto lg:w-[var(--task-panel-w)] lg:border-l lg:border-line lg:shadow-panel"
    >
      <TaskDetail taskId={taskId} mode="panel" onClose={onClose} onOpenPage={() => navigate(`/tasks/${taskId}`)} />
    </aside>
  );
}
